<template>
  <div class="um-image-gallery">
    <nav class="nav">
      <a 
        href="#" 
        class="button" 
        :class="{'button--date': true, 'button--active': 'timestamp' === sortProp}"
        @click.prevent="sortProp = 'timestamp'">
        Date
      </a>
      
      <a 
        v-for="sp in sortProps" :key="sp.prop"
        href="#" 
        class="button" 
        :class="{[`button--${sp.em}`]: true, 'button--active': sp.prop === sortProp}" 
        @click.prevent="sortProp = sp.prop">
        {{sp.label}}
      </a>
    </nav>

    <p v-if="sortProp && sortProp !== 'timestamp'" class="image-gallery__sort-description">
      Below is a list of images taken when people felt <span class="image-gallery__sort-prop-label">{{sortProps.find(p => p.prop === sortProp).label}}</span>
    </p>

    <transition name="fade" v-if="sortedItems" appear>
      <div class="image-gallery">
          <div
            v-for="(item, idx) in sortedItems"
            class="image-gallery-item"
            :key="`${item.id}-${idx}`"
            :id="`image-gallery-item-${item.id}`">

            <div class="image-gallery-item__overlay" @click="currentItem=item">
              {{item.timestamp | shortTime}}
              <template v-if="item.location">
                <br>
                {{item.location.display_name || item.location.full_name || item.location.name}}
              </template>
              <span class="image-gallery-item__overlay-arrow">
                 &#8599;
              </span>
            </div>

            <img
              v-lazy="imageURL(item.image.path, 300)"
              class="image-gallery-item__image"
              :data-id="item.id"
              @load="resizeInstance(item.id)"
            />
          </div>
      </div>
    </transition>

    <div class="loading-container" v-else>
      <app-loading />
    </div>

    <infinite-loading @infinite="infiniteHandler"></infinite-loading>

    <transition name="fade" v-if="currentItem" mode="out-in" appear>
      <div class="modal">
        <div class="modal-bg" @click="currentItem = null"></div>
        <span class="modal-arrow" @click="currentItem = null">
          &#8601;
        </span>
        <div class="modal-content" @click="currentItem = null">
          <img :src="imageURL(currentItem.image.path,1200)" class="modal-content__image"/>
        </div>
        <div class="modal-footer" >
          <um-audio-player v-if="currentItem.audio && currentItem.audio.public" :item="currentItem" />
          <span v-else>{{currentItem | text}}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import Vue from 'vue'
  import VueLazyload from 'vue-lazyload'
  import sortBy from 'lodash/sortBy'
  import compact  from 'lodash/compact'
  import InfiniteLoading from 'vue-infinite-loading'
  import AppLoading from './AppLoading'
  import UMAudioPlayer from './AudioPlayer.vue'

  const shortestTime = (timestamp) => {
    const parts = (new Date(timestamp)).toGMTString().split(' ')
    return `${parts[2]} ${parts[3]}`
  }

  Vue.use(VueLazyload, {
    observer: true,
    error: 'data:image/svg+xml;base64,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'
  })

  function resizeGridItem(item){
    var grid = document.getElementsByClassName('image-gallery')[0];
    var rowHeight = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-auto-rows'));
    var rowGap = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-row-gap'));
    var rowSpan = Math.floor((item.querySelector('.image-gallery-item__image').getBoundingClientRect().height+rowGap)/(rowHeight+rowGap));
      item.style.gridRowEnd = "span "+rowSpan;
  }

  function resizeAllGridItems(){
    var allItems = document.getElementsByClassName("item");
    for(var x=0;x<allItems.length;x++){
      resizeGridItem(allItems[x]);
    }
  }

  function resizeInstance(item){
    resizeGridItem(item);
  }

  export default {
    name: 'UMImageGallery',
    components: {
      AppLoading,
      InfiniteLoading,
      'um-audio-player': UMAudioPlayer
    },
    props: {
      api: {
        type: String,
        required: true
      },
      cdn: {
        type: String,
        required: true
      },
      limit: {
        type: Number,
        default: 20
      }
    },
    computed: {
      sortedItems () {
        const {items, sortProp} = this
        if (!items) return

        const sorted = 
          sortProp
            ? sortBy(items, i => i[sortProp] || i.data[sortProp])
                .filter(i => sortProp === 'timestamp' || (i[sortProp] || i.data[sortProp]) > 3)
            : items

        return sorted.filter(({image}) => !!image).reverse()
      }
    },
    data () {
      return {
        loading: false,
        page: 1,
        counts: null,
        currentItem: null,
        items: null,
        error: null,
        sortProp: 'timestamp',
        sortProps: [
          {em: 'negative', prop: "right_now_i_feel_anxious", label: 'Anxious'},
          {em: 'negative', prop: "right_now_i_feel_stressed", label: 'Stressed'},
          {em: 'negative', prop: "right_now_i_feel_down", label: 'Down'},
          {em: 'negative', prop: "right_now_i_feel_lonely", label: 'Lonely'},
          {em: 'negative', prop: "right_now_i_feel_tired", label: 'Tired'},
          {em: 'positive', prop: "right_now_i_feel_confident", label: 'Confident'},
          {em: 'positive', prop: "right_now_i_feel_relaxed", label: 'Relaxed'},
          {em: 'positive', prop: "right_now_i_feel_happy", label: 'Happy'},
          {em: 'positive', prop: "right_now_i_feel_connected_with_other_people", label: 'Connected'},
          {em: 'positive', prop: "right_now_i_feel_energetic", label: 'Energetic'}
        ]
      }
    },
    mounted () {
      window.addEventListener('resize', resizeAllGridItems);
    },
    beforeDestroy(){
      window.removeEventListener('resize', resizeAllGridItems);
    },
    methods: {
      resizeInstance(id) {
        resizeInstance(document.querySelector(`#image-gallery-item-${id}`))
      },
      imageURL(path, width = 300) {
        const parts = path.split('/')
        const file = parts.pop()
        return `${this.cdn}/assets${parts.join('/')}/${width}/${file}`
      },
      async infiniteHandler($state) {
        if (this.loading) {
          return
        }

        this.loading = true
        try {
          const count = await this.loadNext()
        
          if (count === 0) {
            $state.complete()
            return
          }

          this.page += 1

          resizeAllGridItems()

          if (this.counts && this.page >= Math.ceil(this.counts / this.limit)) {
            $state.complete()
          } else {
            $state.loaded()
          }

          this.loading = false
        } catch (err) {
          this.loading = false
          $state.complete()
        }
      },
      loadNext(){
        const { page, api, limit } = this

        return fetch(api + '/image?limit=' + limit + '&page=' + page)
        .then(resp => resp.json())
        .then(({count, data}) => {
          this.counts = count
          const oldItems = this.items || []
          this.items = oldItems.concat(data)
          return data.length
        })
        .catch(error => {
          this.error = error.message
          throw error
        })
      }
    },
    filters: {
      shortTime (timestamp) {
        const parts = (new Date(timestamp)).toGMTString().split(' ')
        return `${parts[1]} ${parts[2]} ${parts[3]}`
      },
      shortestTime (timestamp) {
        const parts = (new Date(timestamp)).toGMTString().split(' ')
        return `${parts[2]} ${parts[3]}`
      },
      text(item) {
        const {title,location,timestamp} = item
        const locationName = location ? (location.display_name || location.full_name || location.name) : null
        return compact([shortestTime(timestamp), title, locationName]).join(' | ')
      }
    }
  }
</script>

<style>
@import "./styles/button.css";
@import "./styles/fade-transition.css";
@import "./styles/loading-container.css";
@import "./styles/modal.css";
@import "./styles/nav.css";
.um-image-gallery {
  box-sizing: border-box;
  font-family: inherit;
}
.um-image-gallery *,
.um-image-gallery *:after,
.um-image-gallery *:before{
  box-sizing: inherit;
  font-family: inherit;
}

.image-gallery {
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(300px,1fr));
  grid-auto-rows: 24px;
}
.image-gallery-item {
  display: block;
  /*max-width: 100%;
  max-height: 100%;*/
  position: relative;
  overflow: hidden;
}
.image-gallery-item__image[lazy=loading] {
  opacity: 0;
}
.image-gallery-item__image[lazy=loaded] {
  opacity: 1;
}
.image-gallery-item__image {
  display: block;
  width: 100%;
  /*height: auto;*/
  object-fit: cover;
  object-position: center;
  
  transition: all .2s ease;
  margin-bottom: 10px;
}
.image-gallery-item__overlay {
  position: absolute;
  background-color: rgba(221,252,72,.9);
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  z-index: 2;
  opacity: 0;
  will-change: opacity;
  transition: opacity .2s ease;
  color: #343C4C;
  padding: 1rem;
  font-size: 14px;
  cursor: pointer;
  overflow: hidden;
}
.image-gallery-item:hover .image-gallery-item__overlay {
  opacity: 1;
}
.image-gallery-item__overlay-arrow {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  font-size: 2rem;
}
.image-gallery__sort-description {
  text-align: center;
}
.image-gallery__sort-prop-label {
  text-transform: lowercase;
}
</style>
